import * as React from "react";
import { createWebSocketService } from "~/services/websocket";
import { getAccessTokenServer, fetchDriverStateServer } from "~/lib/services/user";
import { useUser } from "~/lib/services/user-frontend";
import { useStayAwake, type AwakeMechanisms } from "~/hooks/useStayAwake";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export enum AppState {
  LOCATION_DENIED = "LOCATION_DENIED",
  READY_TO_RIDE = "READY_TO_RIDE",
  ONLINE = "ONLINE",
  RIDE_IN_PROGRESS = "RIDE_IN_PROGRESS",
  ARRIVED = "ARRIVED",
  UNLOCKED = "UNLOCKED",
  COMPLETED = "COMPLETED",
  CLIENT_CANCELLED = "CLIENT_CANCELLED",
  DRIVER_CANCELLED = "DRIVER_CANCELLED",
  CLOSED = "CLOSED",
}

export enum Screen {
  HOME = "HOME",
  MY_RIDES = "MY_RIDES",
  SUPPORT = "SUPPORT",
  PRIVACY_POLICY = "PRIVACY_POLICY",
  SETTINGS = "SETTINGS",
}

interface RideRequest {
  request_id: string;
  user_id: string;
  latitude: number;
  longitude: number;
  created_at: string;
  status: string;
  customer_address?: string;
  duration?: string;
  distance?: string;
  real_time_duration?: string;
  real_time_distance?: string;
  polyline?: string;
  next_step?: string;
  then_step?: string;
  next_step_maneuver?: string;
  then_step_maneuver?: string;
  ride_type?: string;  // "qr_walkup" for QR walk-up rides
}

interface PairedBike {
  bike_uuid: string;
  device_id: string;
}

type AppContextType = {
  // WebSocket related
  isOnline: boolean;
  goOnline: () => void;
  goOffline: () => void;
  setOnOnlineStatusCallback: (callback: (isOnline: boolean) => void) => void;
  setOnRequestCallback: (callback: (request: any) => void) => void;
  setOnRideProgressUpdateCallback: (callback: (update: any) => void) => void;
  setOnRideCancelledCallback: (callback: () => void) => void;
  wsService: ReturnType<typeof createWebSocketService> | null;

  // App state related
  appState: AppState;
  setAppState: (state: AppState) => void;
  userLocation: { lat: number; lng: number; heading?: number } | null;
  setUserLocation: (location: { lat: number; lng: number; heading?: number } | null) => void;
  errorMsg: string | undefined;
  setErrorMsg: (msg: string | undefined) => void;
  markerLoaded: boolean;
  setMarkerLoaded: (loaded: boolean) => void;

  // Ride request related
  rideRequest: RideRequest | null;
  setRideRequest: (request: RideRequest | null) => void;
  isRequestLoading: boolean;
  rideRequestError: string | null;
  acceptRequest: (requestId: string) => Promise<void>;
  declineRequest: (requestId: string) => Promise<void>;
  cancelRide: (requestId: string) => Promise<void>;
  closeRequest: () => void;
  resetToOnline: () => void;

  // Boot state
  isBooting: boolean;

  // Screen management
  activeScreen: Screen | null;
  setActiveScreen: (screen: Screen | null) => void;
  navigateToScreen: (screen: Screen) => void;
  goBack: () => void;

  // Stay awake
  awakeMechanisms: AwakeMechanisms;
  retryStayAwake: () => void;

  // Bike pairing
  accessToken: string | null;
  pairedBike: PairedBike | null;
  setPairedBike: (bike: PairedBike | null) => void;
};

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  // WebSocket state
  const [wsService, setWsService] = useState<ReturnType<
    typeof createWebSocketService
  > | null>(null);
  const [isOnline, setIsOnline] = useState(false);

  // Keep screen on while driver is online
  const device = useStayAwake();

  useEffect(() => {
    if (isOnline) {
      device.preventSleeping();
    } else {
      device.allowSleeping();
    }
  }, [isOnline]);

  const { data: user } = useUser();


  const onlineStatusCallbackRef = useRef<((isOnline: boolean) => void) | null>(
    null
  );
  const requestCallbackRef = useRef<((request: any) => void) | null>(null);
  const rideProgressUpdateCallbackRef = useRef<((update: any) => void) | null>(
    null
  );
  const rideCancelledCallbackRef = useRef<((data?: any) => void) | null>(null);
  const rideAcceptedByOtherCallbackRef = useRef<((data: any) => void) | null>(null);
  const rideStatusUpdatedCallbackRef = useRef<((data: any) => void) | null>(null);
  const isOfflineRef = useRef(false);
  const activeRequestIdRef = useRef<string | null>(null);

  // Always start CLOSED to avoid flashing the bottom sheet during SSR/hydration.
  // A one-time effect below resolves to READY_TO_RIDE or triggers auto-reconnect → ONLINE.
  const [appState, setAppState] = useState<AppState>(AppState.CLOSED);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
    heading?: number;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [markerLoaded, setMarkerLoaded] = useState(false);

  // Boot state — true until we know whether to reconnect or show ready screen
  const [isBooting, setIsBooting] = useState(true);

  // Bike pairing state
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [pairedBike, setPairedBike] = useState<PairedBike | null>(null);

  // Ride request state — restore from localStorage on init
  const RIDE_REQUEST_KEY = "tory_active_ride_request";
  const [rideRequest, setRideRequest] = useState<RideRequest | null>(() => {
    try {
      const stored = typeof window !== "undefined" ? localStorage.getItem(RIDE_REQUEST_KEY) : null;
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [rideRequestError, setRideRequestError] = useState<string | null>(null);

  // Sync ref + persist whenever rideRequest changes
  useEffect(() => {
    activeRequestIdRef.current = rideRequest?.request_id ?? null;
    console.log(
      "🔍 rideRequest changed:",
      rideRequest ? rideRequest.request_id : "null"
    );
  }, [rideRequest]);

  // Screen management state
  const [activeScreen, setActiveScreen] = useState<Screen | null>(null);

  // Declined requests persistence
  const DECLINED_REQUESTS_KEY = "tory_declined_requests";

  const getDeclinedRequests = useCallback((): string[] => {
    try {
      const stored = localStorage.getItem(DECLINED_REQUESTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const addDeclinedRequest = useCallback((requestId: string) => {
    const declined = getDeclinedRequests();
    if (!declined.includes(requestId)) {
      declined.push(requestId);
      // Keep only last 100 to avoid unbounded growth
      const trimmed = declined.slice(-100);
      localStorage.setItem(DECLINED_REQUESTS_KEY, JSON.stringify(trimmed));
    }
  }, [getDeclinedRequests]);

  const isRequestDeclined = useCallback((requestId: string): boolean => {
    return getDeclinedRequests().includes(requestId);
  }, [getDeclinedRequests]);

  // Ride request actions
  // Persist rideRequest to localStorage whenever it changes
  useEffect(() => {
    try {
      if (rideRequest) {
        localStorage.setItem(RIDE_REQUEST_KEY, JSON.stringify(rideRequest));
      } else {
        localStorage.removeItem(RIDE_REQUEST_KEY);
      }
    } catch { /* quota exceeded — non-critical */ }
  }, [rideRequest]);

  // Helper: set rideRequest AND sync the ref
  const setRideRequestWithRef = useCallback((request: RideRequest | null) => {
    activeRequestIdRef.current = request?.request_id ?? null;
    setRideRequest(request);
  }, []);

  const clearRideRequestError = useCallback(() => {
    setRideRequestError(null);
  }, []);

  const acceptRequest = useCallback(
    async (requestId: string) => {
      if (!wsService) {
        setRideRequestError("WebSocket service not available");
        return;
      }

      setIsRequestLoading(true);
      clearRideRequestError();

      try {
        await wsService.acceptRequest(requestId);
      } catch (error) {
        console.error("Error accepting request:", error);
        setRideRequestError("Failed to accept request. Please try again.");
      } finally {
        setIsRequestLoading(false);
      }
    },
    [wsService, clearRideRequestError]
  );

  const declineRequest = useCallback(
    async (requestId: string) => {
      if (!wsService) {
        setRideRequestError("WebSocket service not available");
        return;
      }

      setIsRequestLoading(true);
      clearRideRequestError();

      try {
        await wsService.declineRequest(requestId);
        addDeclinedRequest(requestId);
        console.log("💾 Saved declined request to localStorage:", requestId);
        setRideRequest(null);
        setAppState(AppState.ONLINE);
        console.log("Switching to online state");
      } catch (error) {
        console.error("Error declining request:", error);
        setRideRequestError("Failed to decline request. Please try again.");
      } finally {
        setIsRequestLoading(false);
      }
    },
    [wsService, clearRideRequestError, addDeclinedRequest]
  );

  const cancelRide = useCallback(
    async (requestId: string) => {
      console.log("🚫 Cancel ride called with requestId:", requestId);
      if (!wsService) {
        setRideRequestError("WebSocket service not available");
        return;
      }

      setIsRequestLoading(true);
      clearRideRequestError();

      try {
        await wsService.cancelRide(requestId);
        console.log("🚫 WebSocket cancel successful, updating state...");
        setRideRequest(null);
        setAppState(AppState.DRIVER_CANCELLED); // Show driver cancelled message first
        console.log(
          "🚫 State updated: rideRequest=null, appState=DRIVER_CANCELLED"
        );
      } catch (error) {
        console.error("Error cancelling ride:", error);
        setRideRequestError("Failed to cancel ride. Please try again.");
      } finally {
        setIsRequestLoading(false);
        console.log("🚫 Cancel ride process completed");
      }
    },
    [wsService, clearRideRequestError]
  );

  const closeRequest = useCallback(() => {
    setRideRequest(null);
    setRideRequestError(null);
    setAppState(AppState.ONLINE);
    console.log("Switching to online state1");
  }, []);

  const resetToOnline = useCallback(() => {
    setRideRequest(null);
    setRideRequestError(null);
    setAppState(AppState.ONLINE);
    console.log("🔄 Resetting to ONLINE state");
  }, []);

  // Ride request event handlers
  const handleRequest = useCallback((message: any) => {
    console.log("Received ride request message:", message);
    if (message.type === "ride_request" && message.data) {
      const requestId = message.data.request_id;

      // Skip requests the driver already declined
      if (requestId && isRequestDeclined(requestId)) {
        console.log("⏭️ Skipping previously declined request:", requestId);
        return;
      }

      setRideRequestWithRef(message.data);
      setRideRequestError(null);

      // Open the bottom sheet if it's minimized (CLOSED state)
      setAppState((prev) => {
        if (prev === AppState.CLOSED) {
          console.log("📬 New ride request received while bottom sheet closed — opening");
          return AppState.ONLINE;
        }
        return prev;
      });
    }
  }, [setRideRequestWithRef, isRequestDeclined]);

  const handleRideProgressUpdate = useCallback(
    (update: any) => {
      setRideRequest((prev) => {
        if (!prev || update.request_id !== prev.request_id) {
          console.log(
            "🔄 No matching ride request found for update:",
            update.request_id
          );
          return prev;
        }
        return {
          ...prev,
          real_time_duration: update.duration,
          real_time_distance: update.distance,
          polyline: update.polyline ?? prev.polyline,
          next_step: update.next_step ?? prev.next_step,
          then_step: update.then_step ?? prev.then_step,
          next_step_maneuver: update.next_step_maneuver ?? prev.next_step_maneuver,
          then_step_maneuver: update.then_step_maneuver ?? prev.then_step_maneuver,
        };
      });
    },
    []
  );

  const handleRideCancelled = useCallback((data?: any) => {
    const cancelledRequestId = data?.request_id;
    const currentRequestId = activeRequestIdRef.current;

    // If the cancellation has a request_id, only act on it if it matches the current request
    if (cancelledRequestId && currentRequestId && cancelledRequestId !== currentRequestId) {
      console.log("⏭️ Ignoring cancellation for different request:", cancelledRequestId, "current:", currentRequestId);
      return;
    }

    // If we don't have a current request, nothing to cancel
    if (!currentRequestId) {
      console.log("⏭️ Ignoring cancellation — no active request");
      return;
    }

    console.log("✅ Ride cancelled, showing client cancelled state for request:", cancelledRequestId || currentRequestId);
    setRideRequestWithRef(null);
    setRideRequestError(null);
    setAppState(AppState.CLIENT_CANCELLED);
  }, [setRideRequestWithRef]);

  const handleRideAcceptedByOther = useCallback(
    (data: any) => {
      console.log("👥 Ride was accepted by another driver:", data);

      // Only clear the request if it matches the one that was accepted
      if (rideRequest && data.request_id === rideRequest.request_id) {
        console.log("✅ Request ID matches current ride, clearing request");
        setRideRequest(null);
        setRideRequestError(null);
        setAppState(AppState.ONLINE);
        console.log("Switching back to online state");
      } else {
        console.log("⚠️ Request ID doesn't match current ride, ignoring");
      }
    },
    [rideRequest]
  );

  const handleRideAccepted = useCallback(
    (acceptedRideData?: any) => {
      console.log(
        "✅ Ride accepted, checking status:",
        acceptedRideData?.status
      );

      // Check the status of the ride to set appropriate app state
      if (acceptedRideData?.status === "UNLOCKED") {
        console.log(
          "🎯 Ride is in UNLOCKED status, setting app state to UNLOCKED"
        );
        setAppState(AppState.UNLOCKED);
      } else if (acceptedRideData?.status === "ARRIVED") {
        console.log(
          "🎯 Ride is in ARRIVED status, setting app state to ARRIVED"
        );
        setAppState(AppState.ARRIVED);
      } else {
        console.log("✅ Ride accepted, setting app state to RIDE_IN_PROGRESS");
        setAppState(AppState.RIDE_IN_PROGRESS);
      }

      // Merge acceptance data with existing ride request to preserve location info
      // Filter out undefined values to avoid overwriting existing data
      if (acceptedRideData) {
        const filteredData = Object.fromEntries(
          Object.entries(acceptedRideData).filter(([_, v]) => v !== undefined)
        );
        console.log("✅ Merging accepted ride data:", filteredData);
        setRideRequest((prev) => prev ? { ...prev, ...filteredData, status: "ACCEPTED" } : acceptedRideData);
      }
    },
    [setRideRequest]
  );

  const handleRideStatusUpdated = useCallback(
    (data: any) => {
      console.log("🔄 Ride status updated:", data);

      // Check if the status was reset to ACCEPTED (customer reported driver hasn't arrived)
      if (data.status === "ACCEPTED") {
        console.log("⚠️ Customer reported driver hasn't arrived - resetting to RIDE_IN_PROGRESS");
        setAppState(AppState.RIDE_IN_PROGRESS);

        // Update ride request status if we have a matching request
        if (rideRequest && data.request_id === rideRequest.request_id) {
          setRideRequest((prev) => prev ? { ...prev, status: "ACCEPTED" } : null);
        }
      }

      // Handle UNLOCKED status - customer has scanned QR code and unlocked the bike
      if (data.status === "UNLOCKED") {
        console.log("🔓 Customer has unlocked the bike - setting app state to UNLOCKED");
        setAppState(AppState.UNLOCKED);

        // Update ride request status if we have a matching request
        if (rideRequest && data.request_id === rideRequest.request_id) {
          setRideRequest((prev) => prev ? { ...prev, status: "UNLOCKED" } : null);
        }
      }
    },
    [rideRequest]
  );

  const initializeWebSocket = useCallback(async () => {
    if (!user?.user_id) {
      console.log("No user ID available, cannot initialize WebSocket");
      return;
    }

    if (wsService) {
      console.log("WebSocket service already exists, reusing...");
      try {
        await wsService.goOnline();
        setIsOnline(true);
        setAppState(AppState.ONLINE);
        console.log("Switching to online state3");

        // Send current location when initializing WebSocket
        if (userLocation) {
          await wsService.updateLocation(userLocation);
        }
      } catch (error) {
        console.error("❌ Failed to initialize existing WebSocket:", error);
        setErrorMsg("Failed to connect to server");
      }
      return;
    }

    if (isOfflineRef.current) {
      console.log("User is intentionally offline, not initializing WebSocket");
      return;
    }

    try {
      const fetchedToken = await getAccessTokenServer();
      if (!fetchedToken) {
        console.error("No access token available, cannot initialize WebSocket");
        setErrorMsg("Authentication required");
        return;
      }

      setAccessToken(fetchedToken);

      console.log("Creating new WebSocket service for user:", user.user_id);
      const service = createWebSocketService(user.user_id, fetchedToken);

      service.setOnOnlineStatusCallback((status) => {
        console.log("WebSocket online status changed:", status);
        setIsOnline(status);
        onlineStatusCallbackRef.current?.(status);
      });

      service.setOnRequestCallback((request) => {
        console.log("Received ride request:", request);
        requestCallbackRef.current?.(request);
      });

      service.setOnRideProgressUpdateCallback((update) => {
        console.log("🔄 Ride progress update received:", update);
        rideProgressUpdateCallbackRef.current?.(update);
      });

      service.setOnRideCancelledCallback((data) => {
        console.log("Ride cancelled", data);
        rideCancelledCallbackRef.current?.(data);
      });

      service.setOnRideAcceptedByOtherCallback((data) => {
        console.log("Ride accepted by another driver", data);
        rideAcceptedByOtherCallbackRef.current?.(data);
      });

      service.setOnRideStatusUpdatedCallback((data) => {
        console.log("Ride status updated", data);
        rideStatusUpdatedCallbackRef.current?.(data);
      });

      service.setOnDriverOnlineStatusCallback((data: { is_online: boolean }) => {
        console.log("🟢 Driver online status from backend:", data);
        if (data.is_online) {
          setIsOnline(true);
          setAppState(AppState.ONLINE);
        }
      });

      service.setOnBikePairedCallback((data: { bike_uuid: string; device_id: string }) => {
        console.log("🔗 Bike paired from backend:", data);
        setPairedBike(data);
      });

      service.setOnBikeUnpairedCallback(() => {
        console.log("🔓 Bike unpaired");
        setPairedBike(null);
      });

      service.setOnAnyMessageCallback(() => {});

      setWsService(service);

      console.log("@@@Going online!@@@");

      await service.goOnline();
      setErrorMsg(undefined);
      setIsOnline(true);
      setAppState(AppState.ONLINE);
      console.log("Switching to online state4");


      if (userLocation) {
        await service.updateLocation(userLocation);
      }
    } catch (error) {
      console.error("Failed to create WebSocket service:", error);
      setErrorMsg("Failed to connect to server");
    }
  }, [user?.user_id, wsService, userLocation]);

  // One-time boot: fetch driver state from backend, then restore or show ready screen
  const hasBooted = useRef(false);
  useEffect(() => {
    if (hasBooted.current || !user?.user_id) return;
    hasBooted.current = true;

    const boot = async () => {
      try {
        const driverState = await fetchDriverStateServer();

        if (driverState?.is_online) {
          console.log("🔄 Driver is online, restoring session...", driverState);

          // Set paired bike from backend state
          if (driverState.paired_bike) {
            setPairedBike(driverState.paired_bike);
          }

          // Connect WebSocket and restore state
          await initializeWebSocket();

          if (driverState.in_ride && driverState.active_ride) {
            setRideRequest(driverState.active_ride);
            const status = driverState.active_ride.status;
            if (status === "UNLOCKED") {
              setAppState(AppState.UNLOCKED);
            } else if (status === "ARRIVED") {
              setAppState(AppState.ARRIVED);
            } else {
              setAppState(AppState.RIDE_IN_PROGRESS);
            }
          } else {
            setRideRequest(null);
            setIsOnline(true);
            setAppState(AppState.ONLINE);
          }
        } else {
          // Driver is offline — just set paired bike if any
          if (driverState?.paired_bike) {
            setPairedBike(driverState.paired_bike);
          }
          setAppState(AppState.READY_TO_RIDE);
        }
      } catch (e) {
        console.error("❌ Failed to fetch driver state on boot:", e);
        setAppState(AppState.READY_TO_RIDE);
      } finally {
        setIsBooting(false);
      }
    };

    boot();
  }, [user?.user_id, initializeWebSocket]);

  const goOnline = useCallback(async () => {
    isOfflineRef.current = false;

    if (wsService) {
      try {
        await wsService.goOnline();
        setIsOnline(true);
        setAppState(AppState.ONLINE);
  
        if (userLocation) {
          await wsService.updateLocation(userLocation);
        }
      } catch (error) {
        setErrorMsg("Failed to connect to server");
      }
    } else {
      await initializeWebSocket();
    }
  }, [wsService, initializeWebSocket, userLocation]);

  const goOffline = useCallback(() => {
    isOfflineRef.current = true;
    console.log("Going offline...");
    if (wsService) {
      wsService.goOffline(); // sends go_offline message + disconnects after 300ms
      setIsOnline(false);
      setWsService(null);
      setAppState(AppState.READY_TO_RIDE);
    }
  }, [wsService]);

  const setOnOnlineStatusCallback = useCallback(
    (callback: (isOnline: boolean) => void) => {
      onlineStatusCallbackRef.current = callback;
    },
    []
  );

  const setOnRequestCallback = useCallback(
    (callback: (request: any) => void) => {
      requestCallbackRef.current = callback;
    },
    []
  );

  const setOnRideProgressUpdateCallback = useCallback(
    (callback: (update: any) => void) => {
      rideProgressUpdateCallbackRef.current = callback;
    },
    []
  );

  const setOnRideCancelledCallback = useCallback((callback: (data?: any) => void) => {
    rideCancelledCallbackRef.current = callback;
  }, []);

  const setOnRideAcceptedByOtherCallback = useCallback((callback: (data: any) => void) => {
    rideAcceptedByOtherCallbackRef.current = callback;
  }, []);

  const setOnRideStatusUpdatedCallback = useCallback((callback: (data: any) => void) => {
    rideStatusUpdatedCallbackRef.current = callback;
  }, []);

  // Set up ride request callbacks
  useEffect(() => {
    setOnRequestCallback(handleRequest);
    setOnRideProgressUpdateCallback(handleRideProgressUpdate);
    setOnRideCancelledCallback(handleRideCancelled);
    setOnRideAcceptedByOtherCallback(handleRideAcceptedByOther);
    setOnRideStatusUpdatedCallback(handleRideStatusUpdated);

    if (wsService?.setOnRideAcceptedCallback) {
      wsService.setOnRideAcceptedCallback(handleRideAccepted);
    }

    if (wsService?.setOnRideAcceptedByOtherCallback) {
      wsService.setOnRideAcceptedByOtherCallback(handleRideAcceptedByOther);
    }

    if (wsService?.setOnRideStatusUpdatedCallback) {
      wsService.setOnRideStatusUpdatedCallback(handleRideStatusUpdated);
    }

    return () => {
      setOnRequestCallback(() => {});
      setOnRideProgressUpdateCallback(() => {});
      setOnRideCancelledCallback(() => {});
      setOnRideAcceptedByOtherCallback(() => {});
      setOnRideStatusUpdatedCallback(() => {});
      if (wsService?.setOnRideAcceptedCallback) {
        wsService.setOnRideAcceptedCallback(() => {});
      }
      if (wsService?.setOnRideAcceptedByOtherCallback) {
        wsService.setOnRideAcceptedByOtherCallback(() => {});
      }
      if (wsService?.setOnRideStatusUpdatedCallback) {
        wsService.setOnRideStatusUpdatedCallback(() => {});
      }
    };
  }, [
    handleRequest,
    handleRideProgressUpdate,
    handleRideCancelled,
    handleRideAccepted,
    handleRideAcceptedByOther,
    handleRideStatusUpdated,
    setOnRequestCallback,
    setOnRideProgressUpdateCallback,
    setOnRideCancelledCallback,
    setOnRideAcceptedByOtherCallback,
    setOnRideStatusUpdatedCallback,
    wsService,
  ]);

  const value = useMemo(
    () => ({
      // WebSocket related
      isOnline,
      goOnline,
      goOffline,
      setOnOnlineStatusCallback,
      setOnRequestCallback,
      setOnRideProgressUpdateCallback,
      setOnRideCancelledCallback,
      wsService,

      // App state related
      appState,
      setAppState,
      userLocation,
      setUserLocation,
      errorMsg,
      setErrorMsg,
      markerLoaded,
      setMarkerLoaded,

      // Ride request related
      rideRequest,
      setRideRequest,
      isRequestLoading,
      rideRequestError,
      acceptRequest,
      declineRequest,
      cancelRide,
      closeRequest,
      resetToOnline,

      // Boot state
      isBooting,

      // Screen management
      activeScreen,
      setActiveScreen,
      navigateToScreen: (screen: Screen) => {
        setActiveScreen(screen);
      },
      goBack: () => {
        setActiveScreen(null);
      },

      // Stay awake
      awakeMechanisms: device.mechanisms,
      retryStayAwake: device.retryFromUserGesture,

      // Bike pairing
      accessToken,
      pairedBike,
      setPairedBike,
    }),
    [
      isOnline,
      goOnline,
      goOffline,
      setOnOnlineStatusCallback,
      setOnRequestCallback,
      setOnRideProgressUpdateCallback,
      setOnRideCancelledCallback,
      wsService,
      appState,
      userLocation,
      errorMsg,
      markerLoaded,
      rideRequest,
      setRideRequest,
      isRequestLoading,
      rideRequestError,
      acceptRequest,
      declineRequest,
      cancelRide,
      closeRequest,
      resetToOnline,
      isBooting,
      activeScreen,
      setActiveScreen,
      device.mechanisms,
      device.retryFromUserGesture,
      accessToken,
      pairedBike,
      setPairedBike,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
